import { StagePill } from 'components/FbUI/StagePill'
import { DealHistory } from 'models/deals'
import type { User } from 'services/users'
import moment from 'moment'
import { Fragment, useMemo } from 'react'
import { Spinner } from 'react-bootstrap'
import { AiOutlinePlus } from 'react-icons/ai'
import { FaQuestion } from 'react-icons/fa'
import { HiPencil } from 'react-icons/hi'
import { MdOutlineBarChart } from 'react-icons/md'
import useApiService from 'services/api'
import { useUsers } from 'services/users'
import s from 'styled-components'
import { formatTwoDecimal, formatUsdDecimal } from 'utils/formatting'

interface ExpandedRowProps {
  dealId: number
  productName?: string
}

const ExpandedDealsRow = (props: ExpandedRowProps) => {
  const api = useApiService()
  const { data: userData } = useUsers()
  const { data, isLoading: loading } = api.useGetDealHistory({
    dealId: props.dealId,
  })

  const dataRow = useMemo(() => {
    const skipFields = [
      'modified',
      'datetime_closed',
      'last_datetime_stage_changed',
    ]

    // When the creating the first field history logs for an instance,
    // a row is created for each field with action_type === "CREATE"
    // Below is hack to prevent rendering multiple create logs.
    // Check: /server/activity/managers.py
    const createLog = data?.find((it) => it.action_type === 'CREATE')
    const initial = []
    if (createLog) {
      initial.push({
        userName: createLog?.user.first_name + ' ' + createLog?.user.last_name,
        date: createLog?.created_at,
        type: props.productName + ' deal',
        action: 'create',
      })
    }

    const newData = data
      ?.filter(
        (it: DealHistory) =>
          !skipFields.includes(it.field_name) && it.action_type !== 'CREATE'
      )
      .map((row: DealHistory) => {
        return mapRow(row, userData)
      })

    if (newData) {
      return [...initial, ...newData].sort((a, b) =>
        moment(b.date).diff(moment(a.date))
      )
    }

    return initial
  }, [data, loading])

  if (loading)
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    )

  return (
    <ExpandedDealsContainer>
      {dataRow.map((row: DealHistoryRow, index: number) => {
        return (
          <Fragment key={index}>
            <DealContainer>
              <RowIcon>
                <DynamicIconRow row={row} />
              </RowIcon>
              <RowName>
                {row?.userName ? row?.userName : '(Name not found)'}
              </RowName>
              <RowAction>{returnActionString(row)}</RowAction>
              <RowType>{row?.type ? row?.type : '(Type not found)'}</RowType>
              <RowResult row={row} />
              <RowDate>
                {row?.date
                  ? 'on ' + moment(row?.date).format('MMM DD YYYY @ h:mm A')
                  : '(Date not found)'}
              </RowDate>
            </DealContainer>
            {index !== dataRow.length - 1 && <VerticalLine />}
          </Fragment>
        )
      })}
    </ExpandedDealsContainer>
  )
}

const returnActionString = (row: DealHistoryRow) => {
  switch (row.action) {
    case 'create':
      return 'created'
    case 'update':
      return 'updated'
    case 'change':
      return 'changed'
    default:
      return ''
  }
}

function DynamicIconRow({ row }: { row: DealHistoryRow }) {
  switch (row.action) {
    case 'create':
      return <AiOutlinePlus size={18} />
    case 'update':
      return <HiPencil size={18} />
    case 'change':
      return <MdOutlineBarChart size={18} />
    default:
      return <FaQuestion size={18} />
  }
}

function RowResult({ row }: { row: DealHistoryRow }) {
  let newResultFrom = row?.resultFrom
  let newResultTo = <ResultToText>{row?.resultTo}</ResultToText>

  if (row?.type === 'sales stage') {
    newResultFrom = (
      <PillResultContainer>
        <StagePill stageId={row?.resultFrom} />
      </PillResultContainer>
    )
    newResultTo = (
      <PillResultContainer>
        <StagePill stageId={row?.resultTo} />
      </PillResultContainer>
    )
  }

  if (row?.resultFrom && row?.resultTo) {
    return (
      <RowAction>
        <ResultContainer>
          from {newResultFrom} to {newResultTo}
        </ResultContainer>
      </RowAction>
    )
  } else if (!row?.resultFrom && row?.resultTo) {
    return (
      <RowAction>
        <ResultContainer>to {newResultTo}</ResultContainer>
      </RowAction>
    )
  } else {
    return null
  }
}

interface DealHistoryRow {
  userName: string
  date: string
  type: string
  action: string
  resultFrom?: any
  resultTo?: any
}

const fieldNameMappings: Record<
  string,
  (row: DealHistory, userData?: User[]) => DealHistoryRow
> = {
  sales_stage_id: (row) => ({
    userName: row?.user?.first_name + ' ' + row?.user?.last_name,
    date: row?.created_at,
    type: 'sales stage',
    action: 'change',
    resultFrom: row?.previous_value,
    resultTo: row?.value,
  }),
  note: (row) => ({
    userName: row?.user?.first_name + ' ' + row?.user?.last_name,
    date: row?.created_at,
    type: 'note',
    action: 'update',
    resultFrom: row?.previous_value,
    resultTo: row?.value,
  }),
  account_owner_id: (row, userData) => {
    const userTo = userData?.find((u) => u.id === row?.value)
    const valueTo = userTo
      ? `${userTo.first_name} ${userTo.last_name}`
      : row?.value
    const userFrom = userData?.find((u) => u.id === row?.previous_value)
    const valueFrom = userFrom
      ? `${userFrom.first_name} ${userFrom.last_name}`
      : row?.previous_value
    return {
      userName: row?.user?.first_name + ' ' + row?.user?.last_name,
      date: row?.created_at,
      type: 'account owner',
      action: 'update',
      resultFrom: valueFrom,
      resultTo: valueTo,
    }
  },
  monthly_volume_override: (row) => ({
    userName: row?.user?.first_name + ' ' + row?.user?.last_name,
    date: row?.created_at,
    type: 'monthly volume',
    action: 'update',
    resultFrom: /[0-9]*\.?[0-9]+/.test(row?.previous_value)
      ? formatTwoDecimal(row?.previous_value ?? 0)
      : row?.previous_value,
    resultTo: /[0-9]*\.?[0-9]+/.test(row?.value)
      ? formatTwoDecimal(row?.value ?? 0)
      : row?.value,
  }),
  monthly_revenue_override: (row) => ({
    userName: row?.user?.first_name + ' ' + row?.user?.last_name,
    date: row?.created_at,
    type: 'monthly revenue',
    action: 'update',
    resultFrom: /[0-9]*\.?[0-9]+/.test(row?.previous_value)
      ? formatUsdDecimal(row?.previous_value)
      : row?.previous_value,
    resultTo: /[0-9]*\.?[0-9]+/.test(row?.value)
      ? formatUsdDecimal(row?.value)
      : row?.value,
  }),
  close_date_override: (row) => {
    let resultFrom = row?.previous_value
    let resultTo = row?.value
    if (moment(resultFrom).isValid()) {
      resultFrom = moment(resultFrom).format('MMM D, YYYY')
    }
    if (moment(resultTo).isValid()) {
      resultTo = moment(resultTo).format('MMM D, YYYY')
    }
    return {
      userName: row?.user?.first_name + ' ' + row?.user?.last_name,
      date: row?.created_at,
      type: 'close date',
      action: 'update',
      resultFrom,
      resultTo,
    }
  },
}

function defaultMapping(row: DealHistory): DealHistoryRow {
  return {
    userName: row?.user?.first_name + ' ' + row?.user?.last_name,
    date: row?.created_at,
    type: row.field_name.replaceAll('_', ' '),
    action: 'change',
  } as DealHistoryRow
}

function mapRow(row: DealHistory, userData?: User[]): DealHistoryRow {
  const handler = fieldNameMappings[row.field_name] || defaultMapping
  return handler(row, userData)
}

const LoadingContainer = s.div`
    margin: 20px 0px;
    margin-left: calc(40vw + 5rem);
`

const ExpandedDealsContainer = s.div`
  padding: 24px 0px 24px 70px;
`

const DealContainer = s.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`

const RowIcon = s.span`
  width: 16px;
  height: 16px;
  color: #087443;
  margin-right: 16px;
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
`

const RowName = s.span`
  color: #171C26;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0.28px;
`

const RowAction = s.span`
  color: #464F60;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.28px;
`

const RowType = s.span`
  color: #087443;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0.28px;
`

const RowDate = s.span`
  color: #687182;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`

const VerticalLine = s.div`
  width: 2px;
  height: 16px;
  background-color: #D5DBE5;
  margin: 8px 0px;
  margin-left: 8px;
`

const ResultContainer = s.span`
  display: flex;
  align-items: flex-start;
  flex-direction: row;
`

const PillResultContainer = s.span`
  margin: 0 5px;
`

const ResultToText = s.span`
  font-weight: 700;
  margin: 0 4px;
`

export default ExpandedDealsRow
